import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { headerManager } from "@/lib/requests";
import {
    useAuthenticationService,
    AuthStatus,
} from "@/composables/authentication";

import { defineStore, storeToRefs } from "pinia";
import {
    Organization,
    useOrganizationsManager,
} from "@/stores/organizations-store";

export const useOrganizationStore = defineStore("organization", () => {
    const route = useRoute();
    const router = useRouter();
    const authenticationService = useAuthenticationService();
    const {
        isLoading: isLoadingOrganizations,
        organizations,
        organizationNames,
    } = useOrganizationsManager();

    const showSelectOrganization = ref(false);
    const organization = ref<Organization>();

    async function selectOrganization() {
        if (!route.meta.requiresOrg) {
            organization.value = undefined;
            return;
        }

        const orgNameParam = route.params.orgName as string;

        if (organization.value && organization.value.name === orgNameParam) {
            return;
        }

        if (!organizations.value) return;

        // Automatically select org if only one
        if (organizations.value.length === 1) {
            organization.value = organizations.value[0];
        } else {
            organization.value = organizations.value.find(
                (org) => org.name === orgNameParam
            );
        }
        if (!organization.value) {
            showSelectOrganization.value = true;
            return;
        }

        // When orgName is undefined while an organization is selected, update
        // the url to include the selected organization. (this can happen when
        // there is only one organization, in which case the user won't be
        // shown the organization picker where clicking a stek updates the
        // url.)
        if (!orgNameParam) {
            router.push(`${organization.value.name}`);
        }

        showSelectOrganization.value = false;
    }

    // Set the header for the WebComponents requests lib.
    watch(
        () => organization.value?.identity,
        (newIdentity) => {
            if (newIdentity) {
                headerManager.updateHeaders({
                    organization: newIdentity,
                });
            }
        },
        { immediate: true }
    );

    watch(
        authenticationService.authStatus,
        async (newAuthStatus) => {
            if (newAuthStatus !== AuthStatus.SIGNED_IN) {
                organization.value = undefined;
            } else {
                await selectOrganization();
            }
        },
        { immediate: true }
    );

    watch(
        () => route.params.orgName,
        (newOrgName, oldOrgName) => {
            if (newOrgName !== oldOrgName) {
                selectOrganization();
            }
        },
        { immediate: true }
    );

    watch(
        () => route.meta.requiresOrg,
        (newRequiresOrg, oldRequiresOrg) => {
            if (!newRequiresOrg) {
                organization.value = undefined;
            } else if (newRequiresOrg !== oldRequiresOrg) {
                selectOrganization();
            }
        },
        { immediate: true }
    );

    watch(
        organizations,
        (newOrgs) => {
            if (!newOrgs?.length) {
                organization.value = undefined;
                return;
            }
            selectOrganization();
        },
        { deep: true, immediate: true }
    );

    return {
        showSelectOrganization,
        organizationNames,
        isLoadingOrganizations,
        organization,
    };
});

export const useOrganizationManager = () => storeToRefs(useOrganizationStore());
